import React, { useContext, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useSearchParams } from 'react-router';
import { WrapperContext } from '../context/WrapperContext';
import { AppContext } from '../window';
import AuthRedirect from '../components/AuthRedirect';
import ActivityFeed from './components/ActivityFeed';
import OfferCountdown from './components/OfferCountdown';
import TrustBadges from './components/TrustBadges';
import ExitIntentPopup from './components/ExitIntentPopup';
import EmailFirstSignup from './EmailFirstSignup';
import { preloadImage } from '../utils/preloadImage';
import { useTranslation } from '../i18n';
import { useTracking } from '../hooks/useTracking';
import { getLandingBackground } from './utils/landingBackground';
import { decodeEpbEmail } from './utils/decodeEpbEmail';

const LandingPageD: React.FC = () => {
    const { setWrapperClasses } = useContext(WrapperContext);
    const { location, whitelabel } = useContext(AppContext);
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const track = useTracking();
    const backgroundSrc = useMemo(
        () => getLandingBackground(searchParams, whitelabel.wantedGender),
        [searchParams, whitelabel.wantedGender]
    );

    const prefilledEmail = useMemo(
        () => decodeEpbEmail(searchParams.get('epb')),
        [searchParams]
    );
    useEffect(() => {
        track('advertiser_page');
    }, []);

    useEffect(() => {
        const handler = () => { track('page_interact'); };
        document.addEventListener('click', handler, { once: true, capture: true });
        return () => { document.removeEventListener('click', handler, { capture: true }); };
    }, []);

    useEffect(() => {
        setWrapperClasses('100vh d-flex');
        return () => { setWrapperClasses(''); };
    }, [setWrapperClasses]);

    useEffect(() => {
        preloadImage(backgroundSrc, 'high');
    }, [backgroundSrc]);

    const handleExitIntentStay = () => {
        const card = document.querySelector('.email-first-signup');
        if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    return (
        <div
            className="landing-page-d flex-grow-1 d-flex align-items-center"
            style={{ position: 'relative' }}
        >
            <img
                src={backgroundSrc}
                alt=""
                aria-hidden="true"
                fetchPriority="high"
                decoding="async"
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    zIndex: 0,
                }}
            />
            <AuthRedirect />
            <ActivityFeed />
            <ExitIntentPopup onStay={handleExitIntentStay} />

            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1,
                }}
            />

            <div className="position-absolute top-0 start-0 end-0 p-2 p-md-4 d-flex justify-content-center" style={{ zIndex: 3 }}>
                <OfferCountdown />
            </div>

            <Container fluid className="px-3 px-md-5" style={{ position: 'relative', zIndex: 2, paddingTop: '60px', paddingBottom: '2rem' }}>
                <Row className="justify-content-center align-items-center">
                    <Col xxl={5} xl={6} lg={7} md={9}>
                        <div className="landing-d-hero text-center text-white mb-4">
                            <h1 className="fw-bold mb-3">
                                {t('landing_d.headline', { location: location?.town?.name || 'your area' })}
                            </h1>
                            <p className="lead mb-0">
                                {t('landing_d.subheadline')}
                            </p>
                        </div>
                        <div className="signup-card rounded-4 shadow-lg p-4 p-md-5">
                            <EmailFirstSignup initialEmail={prefilledEmail} />
                            <TrustBadges className="mt-3" />
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default LandingPageD;
